import {
	callFinishStatusThunk,
	onboardingState,
	startOnboarding,
} from '../../../redux/slices/detSlice';
import { completeStage, selectStage } from '../../../redux/slices/stageSlice';
import {
	faCheck,
	faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import {
	fetchSingleProfile,
	selectProfile,
	updateRetryCount,
} from '../../../redux/slices/profileSlice';
import React, { useEffect, useRef, useState } from 'react';
import {
	selectSession,
	setGuestVerificationLazyLoad,
} from '../../../redux/slices/sessionSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';

import { bypassConfirmLeaveOnce } from '../../../hooks/confirmLeavePageBypass';
import CaptureDoc from './captureDoc';
import CaptureFace from './captureFace';
import { create } from '@incodetech/welcome';
import { DarkButton } from '../../../components/dark-button';
import DocumentListForm from '../../../components/document-list';
import ErrorVerification from './errorVerification';
import FaceLoginForm from './faceLogin';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GuestProfileForm } from '../../../components/guest-profile-form';
import OnboardingModal from './onboardingModal';
import StepContainer from '../stepContainer';
import styles from '../page.module.css';
import SuccessVerification from './successVerfication';
import Swal from 'sweetalert2';
import { t } from 'i18next';
import TakeSelfieModal from '../../det/takeselfie-modal/takeselfie-modal';
import VerificationStepper from '../../../components/verificationSteps';

const CAMERA_PERMISSION_ERROR_TEXT = [
	'permission',
	'denied',
	'notallowederror',
	'not allowed',
	'camera access',
	'blocked',
	'permissions policy',
];

const INCODE_CPP_NOT_READY_ERROR_TEXT = [
	'cpp api',
	"hasn't been initialized",
	'not initialized',
	'unable to set camera info',
];

const isCameraPermissionDeniedError = (error: unknown) => {
	if (!error) return false;

	const valuesToCheck = [
		error,
		typeof error === 'object' && error !== null && 'name' in error
			? (error as { name?: unknown }).name
			: '',
		typeof error === 'object' && error !== null && 'message' in error
			? (error as { message?: unknown }).message
			: '',
		typeof error === 'object' && error !== null && 'error' in error
			? (error as { error?: unknown }).error
			: '',
	]
		.flat()
		.map((value) => String(value).toLowerCase());

	return valuesToCheck.some((value) =>
		CAMERA_PERMISSION_ERROR_TEXT.some((token) => value.includes(token))
	);
};

const isIncodeCppNotInitializedError = (error: unknown) => {
	if (!error) return false;

	const valuesToCheck = [
		error,
		typeof error === 'object' && error !== null && 'name' in error
			? (error as { name?: unknown }).name
			: '',
		typeof error === 'object' && error !== null && 'message' in error
			? (error as { message?: unknown }).message
			: '',
		typeof error === 'object' && error !== null && 'error' in error
			? (error as { error?: unknown }).error
			: '',
	]
		.flat()
		.map((value) => String(value).toLowerCase());

	return valuesToCheck.some((value) =>
		INCODE_CPP_NOT_READY_ERROR_TEXT.some((token) => value.includes(token))
	);
};

const getCameraPermissionState = async (): Promise<
	'granted' | 'denied' | 'prompt' | 'unknown'
> => {
	if (
		typeof navigator === 'undefined' ||
		!('permissions' in navigator) ||
		typeof navigator.permissions?.query !== 'function'
	) {
		return 'unknown';
	}

	try {
		const status = await navigator.permissions.query({
			name: 'camera' as PermissionName,
		});

		if (
			status.state === 'granted' ||
			status.state === 'denied' ||
			status.state === 'prompt'
		) {
			return status.state;
		}
	} catch (err) {
		console.log('Camera permission state is unavailable', err);
	}

	return 'unknown';
};

enum Step {
	INTRO = 0,
	DOCUMENT = 1,
	CAPTURE_DOC = 2,
	FACE = 3,
	PROFILE = 4,
	SUCCESS = 5,
	ERROR = -1,
	FACE_RESULT = 13,
}

const GuestVerification = () => {
	const [errorVerificationConfig, setErrorVerificationConfig] = useState<{
		title?: string;
		msg?: string;
		checks?: string[];
		onTryAgain?: () => void;
		tryAgainLabel?: string;
	} | null>(null);
	const [btnLoading, setBtnLoading] = useState(false);
	const dispatch = useAppDispatch();
	const {
		token,
		reservation,
		propertyInfo,
		countries,
		fields,
		guestVerificationLazyLoad: lazyLoad,
	} = useAppSelector(selectSession);
	const { onboardingToken } = useAppSelector(onboardingState);
	const { stages, stage } = useAppSelector(selectStage);
	const [activeStep, setActiveStep] = useState<Step>(Step.INTRO);
	const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
	const { currentProfile } = useAppSelector(selectProfile);

	const [sdkInitialized, setSdkInitialized] = useState(false);
	const [incode, setIncode] = useState<any>(null);
	const [subStep, setSubStep] = useState<any>('intro');

	const [showRegistrationSheet, setShowRegistrationSheet] = useState(false);
	const [handleErrorAttempts, setHandleErrorAttempts] = useState(0);
	const [faceMatchStatus, setFaceMatchStatus] = useState<
		'success' | 'failed' | null
	>(null);
	const [showFaceResultSheet, setShowFaceResultSheet] = useState(false);
	const [showOnboardingModal, setShowOnboardingModal] = useState(false);
	const [showManualCheckinFrontDesk, setShowManualCheckinFrontDesk] =
		useState(false);
	const [hideStepper, setHideStepper] = useState(false);
	const [isCameraActive, setIsCameraActive] = useState(false);
	const [isExistingUser, setIsExistingUser] = useState(false);
	const [showTakeSelfieModal, setShowTakeSelfieModal] = useState(false);
	const [cameraSessionKey, setCameraSessionKey] = useState(0);
	const [formKey, setFormKey] = useState(Date.now());
	const guestProfileFormKey = `form-${formKey}`;
	/** Stop every active media track on the page so iOS Safari re-prompts for camera permission. */
	const stopAllMediaStreams = () => {
		document.querySelectorAll('video').forEach((videoEl: HTMLVideoElement) => {
			const stream = videoEl.srcObject;
			if (stream instanceof MediaStream) {
				stream.getTracks().forEach((track) => track.stop());
				videoEl.srcObject = null;
			}
		});
	};

	const showCameraPermissionBlockedError = () => {
		setShowTakeSelfieModal(false);
		setShowRegistrationSheet(false);
		setShowFaceResultSheet(false);
		setHideStepper(false);
		setBtnLoading(false);

		setErrorVerificationConfig({
			title: 'Camera Permission Required',
			msg: 'Camera access is blocked on this device. Please allow camera access in your browser settings and try again.',
			checks: [
				'Open your browser site settings',
				'Allow camera access for this site',
				'Return to this page and try again',
			],
			tryAgainLabel: 'Try Camera Again',
			onTryAgain: () => {
				// setErrorVerificationConfig(null);
				// setHideStepper(false);
				// setIsExistingUser(false);
				// setActiveStep(Step.INTRO);
				// setSubStep('intro');
				// setCameraSessionKey((current) => current + 1);

				onRestartOnboarding();
			},
		});
		setActiveStep(Step.ERROR);

		setTimeout(() => {
			dispatch(setGuestVerificationLazyLoad(false));
		}, 3000);
	};

	// useEffect(() => {
	// 	const nextStage = stage?.stepCode || stage?.stageCode;
	// 	if (nextStage === 'SEI05') {
	// 		setActiveStep(Step.PROFILE);
	// 	} else {
	// 		setActiveStep(Step.INTRO);
	// 	}
	// }, [stage]);

	// useEffect(() => {
	// 	if ((onboardingToken?.length ?? 0) > 0) {
	// 		dispatch(setGuestVerificationLazyLoad(true));
	// 		dispatch(
	// 			fetchRetryStatus({
	// 				token,
	// 				sessionToken: onboardingToken,
	// 				profileID: currentProfile?.profileID ?? null,
	// 			})
	// 		)
	// 			.unwrap()
	// 			.then((res: any) => {
	// 				console.log('Retry status = ', res);
	// 				setShowManualCheckinFrontDesk(res?.result === true);
	// 				dispatch(setGuestVerificationLazyLoad(false));
	// 			})
	// 			.catch((err: any) => {
	// 				console.error('Failed to fetch retry status', err);
	// 				dispatch(setGuestVerificationLazyLoad(false));
	// 			});
	// 	} else {
	// 		dispatch(setGuestVerificationLazyLoad(false));
	// 	}
	// }, [onboardingToken, token, currentProfile?.profileID, dispatch]);

	useEffect(() => {
		const handlePageShow = () => {
			const isCameraStepActive =
				(activeStep === Step.INTRO && subStep === 'camera') ||
				activeStep === Step.CAPTURE_DOC ||
				activeStep === Step.FACE;

			if (!isCameraStepActive) return;

			setCameraSessionKey((current) => current + 1);

			void getCameraPermissionState().then((permissionState) => {
				if (permissionState === 'denied') {
					showCameraPermissionBlockedError();
				}
			});
		};

		window.addEventListener('pageshow', handlePageShow);

		return () => {
			window.removeEventListener('pageshow', handlePageShow);
		};
	}, [activeStep, subStep]);

	// useEffect(() => {
	// 	const isFaceLoginCameraStep = activeStep === 0 && subStep === 'camera';

	// 	if (!isFaceLoginCameraStep) return;

	// 	const timer = window.setTimeout(() => {
	// 		console.log('Timeout##');
	// 		const btn = document.querySelector(
	// 			'.IncodeLoginCaptureButtonContainer button'
	// 		) as HTMLButtonElement | null;

	// 		if (btn) {
	// 			btn.style.display = 'block';
	// 		}
	// 	}, 30000);

	// 	return () => window.clearTimeout(timer);
	// }, [activeStep, subStep]);

	useEffect(() => {
		if (activeStep === Step.CAPTURE_DOC) {
			setHideStepper(true);
		} else if (
			(activeStep === Step.FACE ||
				(activeStep === Step.INTRO && subStep === 'camera')) &&
			!showFaceResultSheet
		) {
			setHideStepper(true);
			setBtnLoading(true);
		} else {
			setHideStepper(false);
			setBtnLoading(false);
		}
	}, [activeStep, subStep, showFaceResultSheet]);
	// useEffect(() => {
	// 	if (showOnboardingModal === true) {
	// 		// Let the modal control its own loading state
	// 		// dispatch(setGuestVerificationLazyLoad(false));
	// 	} else {
	// 		// Reset loading when modal closes
	// 		//dispatch(setGuestVerificationLazyLoad(false));
	// 	}
	// }, [showOnboardingModal, dispatch]);

	const handleDocumentUploadSubmission = (result: {
		success: boolean;
		stage: string;
	}) => {
		if (result?.success) {
			setActiveStep(Step.FACE);
		} else {
			console.log('Document upload failed, starting updateRetryCount');
			if (stage === 'passport') {
				// dispatch(setGuestVerificationLazyLoad(true));
				console.log('lazyLoad set to true for passport');
				dispatch(
					updateRetryCount({
						token,
						sessionToken: onboardingToken,
						dto: currentProfile,
						type: stage === 'passport' ? 'ID' : 'SELFIE',
					})
				)
					.unwrap()
					.then((res: any) => {
						console.log('updateRetryCount success for passport', res);
						setActiveStep(Step.INTRO);
						setShowManualCheckinFrontDesk(true);
					})
					.catch((err: any) => {
						console.error('updateRetryCount failed for passport', err);
						// dispatch(setGuestVerificationLazyLoad(false));
					})
					.finally(() => {
						dispatch(setGuestVerificationLazyLoad(false));
					});
			} else {
				dispatch(setGuestVerificationLazyLoad(true));
				console.log('lazyLoad set to true for other');
				dispatch(
					updateRetryCount({
						token,
						sessionToken: onboardingToken,
						dto: currentProfile,
						type: stage === 'front' || stage === 'back' ? 'ID' : 'SELFIE',
					})
				)
					.then((res: any) => {
						console.log('updateRetryCount success for other', res);
						setActiveStep(Step.INTRO);
						setShowManualCheckinFrontDesk(true);
						// setTimeout(() => {
						// 	dispatch(setGuestVerificationLazyLoad(false));
						// }, 1000);
					})
					.catch((err: any) => {
						console.error('updateRetryCount failed for other', err);
						// dispatch(setGuestVerificationLazyLoad(false));
					})
					.finally(() => {
						dispatch(setGuestVerificationLazyLoad(false));
					});
			}
		}
	};

	const handleFaceVerification = (response: any) => {
		dispatch(setGuestVerificationLazyLoad(true));
		console.log('Face result = ', response);
		const isMatched = response?.confidence >= 0.75;
		setFaceMatchStatus('success');
		// if (isMatched) {
		// 	setFaceMatchStatus('success');
		// } else {
		// 	// setFaceMatchStatus('failed');
		// 	setFaceMatchStatus('success');
		// }

		setActiveStep(Step.FACE_RESULT);
		setShowFaceResultSheet(true);
		setBtnLoading(false);

		setTimeout(() => {
			// setHideStepper(true);
			dispatch(setGuestVerificationLazyLoad(false));
		}, 3000);
	};

	const handleLoginFaceVerification = (response: any) => {
		try {
			if (onboardingToken) {
				dispatch(setGuestVerificationLazyLoad(true));
				dispatch(
					callFinishStatusThunk({
						token,
						sessionToken: onboardingToken,
						profileId: currentProfile?.profileID ?? null,
					})
				)
					.unwrap()
					.then((res: any) => {
						console.log('Finish status response = ', res);
						// setFaceMatchStatus(null);
						// console.log('Finish status response = ', res);
						// setActiveStep(4); // Move to success screen after calling finish status, regardless of the response
						// dispatch(setGuestVerificationLazyLoad(false));
					});
				// Proceed to next step regardless of API success or failure, as the guest has already been verified at this point
			}
		} catch (err: any) {
			console.error('Failed to call finish status', err);
		} finally {
			// proceedToNextStep();
		}
	};

	const createIncodeSession = async (
		onboardtoken: string
	): Promise<boolean> => {
		const baseUrl = process.env.REACT_APP_API_BACKEND_BASE_URL;

		if (!baseUrl) {
			console.error('REACT_APP_API_BACKEND_BASE_URL is missing');
			return false;
		}

		const incodeClient = create({
			apiURL: `${baseUrl}/incode/proxy/${encodeURIComponent(
				onboardtoken ?? ''
			)}`,
			customHeaders: token ? { Authorization: `Bearer ${token}` } : undefined,
			httpRequestTimeout: 300000, // Set a timeout for HTTP requests (optional)
		});

		if (incodeClient.isDesktop()) {
			alert(
				'Desktop environment detected. Please use a mobile device for guest verification.'
			);
			return false;
		}

		try {
			await incodeClient.sendFingerprint({ token: onboardtoken });
		} catch (err) {
			console.error('Failed to register device fingerprint', err);
		}

		setIncode(incodeClient);
		return true;
		// try {
		// 	await incodeClient.initialize();
		// 	return true;
		// } catch (err) {
		// 	console.error('SDK initialization failed:', err);
		// 	Swal.fire({
		// 		title: 'Error',
		// 		text: 'Some error occurred, try again',
		// 		icon: 'error',
		// 		confirmButtonText: 'OK',
		// 	});
		// 	return false;
		// }
	};

	const initializeSDK = React.useCallback(async (): Promise<boolean> => {
		if (!incode || typeof incode.initialize !== 'function') {
			console.error('Incode SDK instance is not ready for initialization');
			return false;
		}

		try {
			await incode.initialize();
			return true;
		} catch (err) {
			console.error('SDK initialization failed:', err);
			Swal.fire({
				title: 'Error',
				text: 'Some error occurred, try again',
				icon: 'error',
				confirmButtonText: 'OK',
			});
			return false;
		}
	}, [incode]);

	const startGuestOnboardingFlow = async (): Promise<boolean> => {
		dispatch(setGuestVerificationLazyLoad(true));

		try {
			const res = await dispatch(
				startOnboarding({
					token,
					profileId: currentProfile?.profileID ?? null,
					confirmationNo: reservation?.PmsConfirmationNo ?? '',
				})
			).unwrap();

			if (!res || !res?.token) {
				console.error('Onboarding API did not return a token');
				return false;
			}

			const success = await createIncodeSession(res.token);
			if (success) {
				setSdkInitialized(true);
			}
			// Important: Reset lazy load whether success or failure
			// setTimeout(() => {
			// 	dispatch(setGuestVerificationLazyLoad(false));
			// }, 3000);

			return success;
		} catch (err: any) {
			console.error('Failed to start onboarding', err);
			// setTimeout(() => {
			// 	dispatch(setGuestVerificationLazyLoad(false));
			// }, 3000);
			setShowTakeSelfieModal(false);
			setShowManualCheckinFrontDesk(true);
			stopLazyLoad();
			return false;
		}
	};

	const stepIndexMap = {
		[Step.INTRO]: 0,

		[Step.FACE]: 0,
		[Step.FACE_RESULT]: 0,

		[Step.DOCUMENT]: 1,
		[Step.CAPTURE_DOC]: 1,

		[Step.PROFILE]: 2,
		[Step.SUCCESS]: 2,
		[Step.ERROR]: 2,
	};
	const mappedActiveStep = stepIndexMap[activeStep] ?? 0;
	const completedStepMap: Partial<Record<Step, number[]>> = {
		[Step.INTRO]: [],
		[Step.FACE]: [0],
		[Step.FACE_RESULT]: faceMatchStatus === 'success' ? [0, 1] : [0],
		[Step.DOCUMENT]: [0],
		[Step.CAPTURE_DOC]: [0],
		[Step.PROFILE]: [0, 1],
		[Step.SUCCESS]: [0, 1, 2],
		[Step.ERROR]: [0, 1],
	};
	const completedSteps = completedStepMap[activeStep] ?? [];

	const getFailedStep = () => {
		// Global error (final stage failure)
		if (activeStep === Step.ERROR) {
			return stepIndexMap[Step.ERROR];
		}

		// Face failure
		if (activeStep === Step.FACE_RESULT && faceMatchStatus === 'failed') {
			return stepIndexMap[Step.FACE_RESULT];
		}

		return null;
	};
	// useEffect(() => {
	// 	// if (!token || !reservation?.PmsConfirmationNo) return;
	// 	// if (hasStartedOnboardingRef.current) return;
	// 	// hasStartedOnboardingRef.current = true;
	// 	// let mounted = true;
	// 	// void startGuestOnboardingFlow().then((success) => {
	// 	// 	if (!mounted || !success) return;
	// 	// });
	// 	// return () => {
	// 	// 	mounted = false;
	// 	// };
	// }, [
	// 	dispatch,
	// 	token,
	// 	reservation?.PmsConfirmationNo,
	// 	currentProfile?.profileID,
	// ]);

	// useEffect(() => {
	// 	if (activeStep > 4) {
	// 		dispatch(
	// 			completeStage({
	// 				token,
	// 				stageID: stage?.stepID,
	// 				ReservationID: reservation?.ReservationID,
	// 				type: 'checkin',
	// 				confirmationNo: reservation?.PmsConfirmationNo,
	// 				stages,
	// 				propertyCode: propertyInfo?.propertyCode ?? '',
	// 				profileID: currentProfile?.profileID ?? null,
	// 			})
	// 		);
	// 	}
	// }, [
	// 	activeStep,
	// 	dispatch,
	// 	token,
	// 	stage?.stepID,
	// 	reservation,
	// 	stages,
	// 	propertyInfo?.propertyCode,
	// 	currentProfile?.profileID,
	// ]);

	const incodeRef = useRef<any>(null);
	//const profileRef = useRef<any>(null);
	const guestRef = useRef<any>(null);
	// const hasStartedOnboardingRef = useRef(false);

	const handleStartVerification = () => {
		// dispatch(setGuestVerificationLazyLoad(false));
		// setBtnLoading(true);
		incodeRef.current?.callIdentityVerification();
		setTimeout(() => {
			setBtnLoading(true);
			dispatch(setGuestVerificationLazyLoad(false));
		}, 3000);
	};

	const handleSuccess = async (data: any) => {
		console.log('SUCCESS in parent', data);
		setBtnLoading(false);
		setIsExistingUser(data ?? false);
		dispatch(setGuestVerificationLazyLoad(true));
		// await dispatch(
		// 	fetchSingleProfile({
		// 		token,
		// 		confirmationNo: reservation.PmsConfirmationNo,
		// 		profileId: currentProfile.profileID,
		// 	})
		// ).unwrap();

		setHandleErrorAttempts(0);
		setActiveStep(Step.PROFILE);
		// setTimeout(() => {
		// 	dispatch(setGuestVerificationLazyLoad(false));
		// }, 3000);
	};

	const handleError = (err: any) => {
		console.log('ERROR in parent', err);

		if (isCameraPermissionDeniedError(err)) {
			showCameraPermissionBlockedError();
			return;
		}

		if (isIncodeCppNotInitializedError(err)) {
			setBtnLoading(false);
			setSubStep('intro');
			setShowTakeSelfieModal(false);
			setSdkInitialized(false);
			setIncode(null);
			setCameraSessionKey((current) => current + 1);
			dispatch(setGuestVerificationLazyLoad(false));

			Swal.fire({
				title: 'Camera Initialization Failed',
				text: 'Please try again. If this keeps happening, refresh the page and allow camera access.',
				icon: 'error',
				confirmButtonText: 'OK',
			});
			return;
		}

		setHandleErrorAttempts((prev) => {
			const nextAttempt = prev + 1;

			const isFinalAttempt = nextAttempt > 3;

			// Common reset (for both retry + final)
			setSubStep('intro');

			if (!isFinalAttempt) {
				setShowRegistrationSheet(true);
			}

			// Always run cleanup (retry + final)
			setTimeout(() => {
				// Handle final attempt AFTER cleanup
				if (isFinalAttempt) {
					setHideStepper(false);
					setShowRegistrationSheet(false);
					setActiveStep(Step.DOCUMENT);
				}
				setBtnLoading(false);
				setErrorVerificationConfig(null);
				dispatch(setGuestVerificationLazyLoad(false));
			}, 2000);

			return nextAttempt;
		});

		// Optional API retry tracking
		/*
	dispatch(
		updateRetryCount({
			token,
			sessionToken: onboardingToken,
			dto: currentProfile,
			type: 'SELFIE',
		})
	);
	*/
	};
	const handleFaceError = (err: any) => {
		console.log('ERROR in handleFaceError', err);

		if (isCameraPermissionDeniedError(err)) {
			showCameraPermissionBlockedError();
			return;
		}

		if (isIncodeCppNotInitializedError(err)) {
			setBtnLoading(false);
			setSubStep('intro');
			setShowTakeSelfieModal(false);
			setSdkInitialized(false);
			setIncode(null);
			setCameraSessionKey((current) => current + 1);
			dispatch(setGuestVerificationLazyLoad(false));
			return;
		}

		dispatch(setGuestVerificationLazyLoad(true));
		setBtnLoading(false);

		dispatch(
			updateRetryCount({
				token,
				sessionToken: onboardingToken,
				dto: currentProfile,
				type: 'SELFIE',
			})
		)
			.then((res: any) => {
				console.log('Retry count updated after document upload failure', res);
				// bypassConfirmLeaveOnce();
				// window.location.reload();
				setActiveStep(Step.INTRO);
				setShowManualCheckinFrontDesk(true);
				setTimeout(() => {
					dispatch(setGuestVerificationLazyLoad(false));
				}, 2000);
				// setFaceMatchStatus('failed');
			})
			.finally(() => {
				setTimeout(() => {
					dispatch(setGuestVerificationLazyLoad(false));
				}, 2000);
			});
	};
	// const resetToInitialStep = () => {
	// 	setErrorVerificationConfig(null);
	// 	setActiveStep(Step.INTRO);
	// 	setHideStepper(false);
	// 	setSubStep('intro');
	// 	setCameraSessionKey((current) => current + 1);
	// };

	const stopLazyLoad = () => {
		setTimeout(() => {
			dispatch(setGuestVerificationLazyLoad(false));
		}, 2000);
	};

	const showErrorConfig = (msg: string) => {
		setErrorVerificationConfig({
			title: 'Profile Onboarding',
			msg,
			checks: [],
			tryAgainLabel: 'Try Again',
			onTryAgain: () => {
				onRestartOnboarding();
			},
		});
		setHideStepper(true);
		setActiveStep(Step.ERROR);
		stopLazyLoad();
	};
	const handleGuestProfileSubmitError = (res: any) => {
		dispatch(setGuestVerificationLazyLoad(true));
		setShowOnboardingModal(false);
		setShowRegistrationSheet(false);
		setShowFaceResultSheet(false);
		setHideStepper(true);

		const message = res?.message?.toLowerCase() || '';

		let errorMsg = res?.message || 'Some error occurred';

		if (message.includes('pending')) {
			errorMsg =
				'Onboarding is pending. Please wait for 30 minutes and try again.';
		} else if (message.includes('profile already exists')) {
			errorMsg = 'Profile already exists. Please rescan again.';
		}
		// ✅ ALWAYS call retry (no returns anywhere)
		// postRetryCount('PROFILE', errorMsg);

		setErrorVerificationConfig({
			title: 'Profile Onboarding',
			msg: errorMsg,
			checks: [],
			tryAgainLabel: 'Try Again',
			onTryAgain: () => {
				postRetryCount('PROFILE', errorMsg);
			},
		});
		setActiveStep(Step.ERROR);
		stopLazyLoad();
	};
	// const handleGuestProfileSubmitError1 = (res: any) => {
	// 	setShowOnboardingModal(false);
	// 	setShowRegistrationSheet(false);
	// 	setShowFaceResultSheet(false);

	// 	// if (res?.message && res.message.toLowerCase().includes('rescan')) {
	// 	// 	Swal.fire({
	// 	// 		icon: 'error',
	// 	// 		title: 'Invalid Document',
	// 	// 		text: res.message,
	// 	// 		confirmButtonText: 'OK',
	// 	// 	}).then(() => {
	// 	// 		setActiveStep(-1);
	// 	// 	});
	// 	// 	return;
	// 	// } else

	// 	if (res?.message && res.message.toLowerCase().includes('pending')) {
	// 		setErrorVerificationConfig({
	// 			title: 'Profile Onboarding',
	// 			msg: 'Onboarding is pending. Please wait for 30 minutes, and retry again..',
	// 			checks: [],
	// 			tryAgainLabel: 'Try Again',
	// 			onTryAgain: () => {
	// 				// setHideStepper(false);
	// 				// setErrorVerificationConfig(null);
	// 				// setActiveStep(Step.INTRO);
	// 				// setSubStep('intro');
	// 				// setCameraSessionKey((current) => current + 1);
	// 				onRestartOnboarding();
	// 			},
	// 		});
	// 		setActiveStep(Step.ERROR);
	// 		setTimeout(() => {
	// 			dispatch(setGuestVerificationLazyLoad(false));
	// 		}, 2000);
	// 		return;
	// 	}

	// 	if (
	// 		res?.message &&
	// 		res.message.toLowerCase().includes('profile already exists')
	// 	) {
	// 		setErrorVerificationConfig({
	// 			title: 'Profile Onboarding',
	// 			msg: 'Profile already exists. Please rescan again.',
	// 			checks: [],
	// 			tryAgainLabel: 'Try Again',
	// 			onTryAgain: () => {
	// 				// setHideStepper(false);
	// 				// setErrorVerificationConfig(null);
	// 				// setActiveStep(Step.INTRO);
	// 				// setSubStep('intro');
	// 				// setCameraSessionKey((current) => current + 1);
	// 				onRestartOnboarding();
	// 			},
	// 		});
	// 		setActiveStep(Step.ERROR);
	// 		setTimeout(() => {
	// 			dispatch(setGuestVerificationLazyLoad(false));
	// 		}, 2000);
	// 		return;
	// 	}

	// 	if (res?.message && res?.message?.length > 0) {
	// 		setErrorVerificationConfig({
	// 			title: 'Profile Onboarding',
	// 			msg: res?.message,
	// 			checks: [],
	// 			tryAgainLabel: 'Try Again',
	// 			onTryAgain: () => {
	// 				// setHideStepper(false);
	// 				// setErrorVerificationConfig(null);
	// 				// setActiveStep(Step.INTRO);
	// 				// setSubStep('intro');
	// 				// setCameraSessionKey((current) => current + 1);
	// 				onRestartOnboarding();
	// 			},
	// 		});
	// 		setActiveStep(Step.ERROR);
	// 		setTimeout(() => {
	// 			dispatch(setGuestVerificationLazyLoad(false));
	// 		}, 2000);
	// 		return;
	// 	}

	// 	const nextAttempts = profileSaveAttempts + 1;
	// 	setProfileSaveAttempts(nextAttempts);

	// 	if (nextAttempts < 3) {
	// 		Swal.fire({
	// 			icon: 'error',
	// 			title: 'Profile Failed to Submit',
	// 			text: res?.message || 'Some error occurred',
	// 			confirmButtonText: 'Retry',
	// 		});
	// 	} else {
	// 		// Maximum number of tries reached
	// 		dispatch(
	// 			updateRetryCount({
	// 				token,
	// 				sessionToken: onboardingToken,
	// 				dto: currentProfile,
	// 				type: 'PROFILE',
	// 			})
	// 		).then(() => {
	// 			Swal.fire({
	// 				icon: 'error',
	// 				title: 'Maximum number of tries reached',
	// 				text: 'Maximum login attempts reached.',
	// 				confirmButtonText: 'Go Back',
	// 			}).then(() => {
	// 				bypassConfirmLeaveOnce();
	// 				window.location.reload();
	// 			});
	// 		});
	// 	}
	// };

	const postRetryCount = (type: string, errorMsg: string) => {
		dispatch(setGuestVerificationLazyLoad(true));
		dispatch(
			updateRetryCount({
				token,
				sessionToken: onboardingToken,
				dto: currentProfile,
				type,
			})
		)
			.unwrap()
			.then(async (res) => {
				if (res !== null && res.data === true) {
					// setActiveStep(Step.INTRO);
					setShowManualCheckinFrontDesk(true);
					stopLazyLoad();
				} else {
					setShowManualCheckinFrontDesk(false);
					onRestartOnboarding();
				}

				// Swal.fire({
				// 	icon: 'error',
				// 	title: 'Profile Failed to Submit',
				// 	text: 'Some error occured',
				// 	confirmButtonText: 'Go Back',
				// }).then(() => {
				// 	bypassConfirmLeaveOnce();
				// 	window.location.reload();
				// });
			});
	};
	// const handleParentSubmitClick = () => {
	// 	profileRef.current?.submitForm();
	// };

	const handleProfileParentSubmitClick = () => {
		guestRef.current?.handleSubmit();
	};

	const handleSdkInitialized = async () => {
		if (!token || !reservation?.PmsConfirmationNo) return;
		setBtnLoading(true);
		const mounted = true;
		setHideStepper(true);

		// 📣 Stop any lingering camera streams before re-initializing (critical for iOS Safari)
		stopAllMediaStreams();
		// Reset SDK state so a fresh instance is created — iOS needs a brand-new getUserMedia call
		setIncode(null);
		setSdkInitialized(false);

		const success = await startGuestOnboardingFlow();

		if (!mounted || !success) {
			setBtnLoading(false);
			return;
		}

		// Intentionally leaving btnLoading as true so the footer button shows loading during the camera step
		setShowTakeSelfieModal(false);
		setCameraSessionKey((current) => current + 1);
		setSubStep('camera');
	};

	const handleCallFinishStatus = async () => {
		try {
			if (onboardingToken) {
				dispatch(setGuestVerificationLazyLoad(true));
				await dispatch(
					callFinishStatusThunk({
						token,
						sessionToken: onboardingToken,
						profileId: currentProfile?.profileID ?? null,
					})
				).unwrap();
				setFaceMatchStatus(null);
				setShowOnboardingModal(true);
				// Proceed to next step regardless of API success or failure, as the guest has already been verified at this point
			}
		} catch (err: any) {
			console.error('Failed to call finish status', err);
		} finally {
			// proceedToNextStep();
		}
	};

	const proceedToNextStep = () => {
		console.log('active step = ' + activeStep);
		/* eslint-disable no-debugger */
		// debugger;

		if (activeStep === Step.INTRO && subStep === 'intro') {
			setBtnLoading(true);
			setShowTakeSelfieModal(true);
			return;
		}
		if (activeStep === Step.INTRO && subStep === 'camera') {
			setShowTakeSelfieModal(false);
			setBtnLoading(true);
			if (sdkInitialized) {
				handleStartVerification();
			}
			return;
		}
		if (activeStep === Step.DOCUMENT) {
			if (selectedDocument) {
				setActiveStep(Step.CAPTURE_DOC);
			}
			return;
		}

		if (activeStep === Step.PROFILE) {
			handleProfileParentSubmitClick();
		}

		if (activeStep === Step.SUCCESS) {
			// dispatch(setGuestVerificationLazyLoad(false));
			// bypassConfirmLeaveOnce();
			// window.location.reload();

			// return;
			dispatch(setGuestVerificationLazyLoad(true));
			dispatch(
				completeStage({
					token,
					stageID: stage?.stepID,
					ReservationID: reservation?.ReservationID,
					type: 'checkin',
					confirmationNo: reservation?.PmsConfirmationNo,
					stages,
					propertyCode: propertyInfo?.propertyCode ?? '',
					profileID: currentProfile?.profileID ?? null,
				})
			)
				.unwrap()
				.then((res: any) => {
					setTimeout(() => {
						dispatch(setGuestVerificationLazyLoad(false));
					}, 2000);
					// bypassConfirmLeaveOnce();
					// window.location.reload();
					return;
				})
				.catch((err: any) => {
					console.error('Failed to complete stage', err);
				});
		}
	};

	const showContinueButton =
		activeStep !== Step.ERROR && activeStep !== Step.CAPTURE_DOC;

	const isDocumentStepBlocked =
		activeStep === Step.DOCUMENT && !selectedDocument;
	const totalGuestCount =
		(reservation?.AdultCount ?? 1) + (reservation?.ChildCount ?? 0);
	const hasMultipleGuests = totalGuestCount > 1;

	const footerButtonLoading = btnLoading || lazyLoad;

	const onRestartOnboarding = () => {
		setShowOnboardingModal(false);
		// Reset states
		setIsExistingUser(false);

		setFaceMatchStatus(null);
		setSelectedDocument(null);
		setShowFaceResultSheet(false);
		setSdkInitialized(false);
		setIncode(null);
		setHideStepper(false);
		setCameraSessionKey((current) => current + 1);
		setFormKey(Date.now());
		// Allow onboarding to start again
		// hasStartedOnboardingRef.current = false;
		// Start onboarding again
		// const success = await startGuestOnboardingFlow();

		// if (success) {

		// }
		setSubStep('intro');
		setActiveStep(Step.INTRO);
		stopLazyLoad();
		return;
	};

	const handleOnboardingCompleted = async () => {
		try {
			if (
				token &&
				reservation?.PmsConfirmationNo &&
				currentProfile?.profileID
			) {
				dispatch(setGuestVerificationLazyLoad(true));
				// await dispatch(
				// 	fetchSingleProfile({
				// 		token,
				// 		confirmationNo: reservation.PmsConfirmationNo,
				// 		profileId: currentProfile.profileID,
				// 	})
				// ).unwrap();
			}
		} catch (error) {
			console.error('Failed to refresh profile after onboarding:', error);
		} finally {
			setShowOnboardingModal(false);
			setActiveStep(Step.PROFILE);
			dispatch(setGuestVerificationLazyLoad(false));
		}
	};

	return (
		<div className="d-flex flex-column w-100" style={{}}>
			<div className="d-flex flex-column">
				<div
					className="d-flex justify-content-center align-items-center w-100 theme-header"
					style={{ minHeight: '80px' }}
				/>

				<div className="flex w-100 mb-2">
					<div className="flex-grow-1">
						{!hideStepper && (
							<VerificationStepper
								activeStep={mappedActiveStep}
								failedStep={getFailedStep()}
								completedSteps={completedSteps}
							/>
						)}

						{showOnboardingModal ? (
							<OnboardingModal
								show={showOnboardingModal}
								reservation={reservation}
								onCompleted={handleOnboardingCompleted}
								onRescan={() => {
									setShowOnboardingModal(false);
									onRestartOnboarding();
								}}
							/>
						) : (
							<>
								{activeStep === Step.INTRO && subStep === 'intro' && (
									<StepContainer active={true}>
										<>
											{console.log('step', subStep)}
											<div
												className="intro-card d-flex flex-column 
							align-items-center text-center"
												role="button">
												<img
													src={process.env.PUBLIC_URL + '/take-selfie.png'}
													alt="Take Selfie"
													style={{ height: '250px' }}
													className="img-fluid"
												/>

												<h2 className="mb-2 fw-bold">
													Get Ready for Your Selfie
												</h2>

												<small className="text-muted">
													We’re about to take a selfie to verify your identity.
													Please keep a neutral facial expression and make sure
													your face is clearly visible.
												</small>
											</div>
										</>
									</StepContainer>
								)}

								{activeStep === Step.INTRO && subStep === 'camera' && (
									<StepContainer active={true}>
										<>
											{console.log('step', subStep)}
											<FaceLoginForm
												key={`face-login-${cameraSessionKey}`}
												ref={incodeRef}
												onSuccess={async (data) => {
													await handleSuccess(data);
												}}
												onError={handleError}
												incode={incode}
												initializeSDK={initializeSDK}
												onSubStepChange={(subStep) =>
													setHideStepper(subStep === 'camera')
												}
											/>

											{/* <CaptureFace
											onComplete={handleLoginFaceVerification}
											onErrorFace={handleFaceError}
											incode={incode}
											type="login"
										/> */}
										</>
									</StepContainer>
								)}

								{activeStep === Step.DOCUMENT && (
									<StepContainer active={true}>
										<>
											{console.log('step', 'document')}
											<DocumentListForm
												onSelect={(doc) => {
													setSelectedDocument(doc);
												}}
											/>
										</>
									</StepContainer>
								)}
								{activeStep === Step.CAPTURE_DOC && selectedDocument && (
									<StepContainer active={true}>
										<>
											{console.log('step', 'CaptureDoc')}
											<CaptureDoc
												key={`capture-doc-${cameraSessionKey}`}
												docType={selectedDocument}
												onComplete={handleDocumentUploadSubmission}
												incode={incode}
												onCameraStateChange={setIsCameraActive}
												initializeSDK={initializeSDK}
											/>
										</>
									</StepContainer>
								)}
								{activeStep === Step.FACE && (
									<StepContainer active={true}>
										<>
											{console.log('step', 'CaptureFace')}
											<CaptureFace
												key={`capture-face-${cameraSessionKey}`}
												onComplete={handleFaceVerification}
												onErrorFace={handleFaceError}
												incode={incode}
												type="selfie"
												initializeSDK={initializeSDK}
											/>
										</>
									</StepContainer>
								)}
								{activeStep === Step.FACE_RESULT && showFaceResultSheet && (
									<StepContainer active={true}>
										<>
											{console.log('step', 'showFaceResultSheet')}
											<div
												className="d-flex justify-content-center align-items-center px-3"
												style={{
													minHeight: hideStepper
														? 'calc(100dvh - 120px)'
														: 'calc(100dvh - 200px)',
													paddingTop: hideStepper ? '0px' : '16px',
													paddingBottom:
														'max(20px, env(safe-area-inset-bottom))',
												}}>
												<div
													className="card shadow-sm border-0 w-100"
													style={{ maxWidth: '520px', borderRadius: '16px' }}>
													<div className="card-body d-flex flex-column align-items-center text-center p-4">
														<div className="mb-3">
															<div
																className={`rounded-circle d-flex align-items-center justify-content-center ${
																	faceMatchStatus === 'success'
																		? 'bg-success-subtle text-success'
																		: 'bg-danger-subtle text-danger'
																}`}
																style={{
																	width: '64px',
																	height: '64px',
																	fontSize: '24px',
																}}>
																<FontAwesomeIcon
																	icon={
																		faceMatchStatus === 'success'
																			? faCheck
																			: faExclamationTriangle
																	}
																/>
															</div>
														</div>

														<h5 className="mb-2 fw-bold">
															{faceMatchStatus === 'success'
																? t('Document submitted')
																: t('Document Processing Failed')}
														</h5>

														<p className="text-muted small mb-4">
															{faceMatchStatus === 'success'
																? t(
																		'Document has been successfully submitted. Please continue with the check-in process.'
																  )
																: t(
																		"We couldn't verify the guest's identity. Please try again or register."
																  )}
														</p>

														{/* <div className="d-flex flex-column gap-3 w-100">
												{faceMatchStatus === 'success' ? (
													<button
														className="btn btn-success w-100"
														onClick={() => {
															setShowFaceResultSheet(false);
															handleCallFinishStatus();
														}}>
														Continue Check-In
													</button>
												) : (
													<button
														className="btn btn-primary w-100"
														onClick={() => {
															setShowFaceResultSheet(false);
															setActiveStep(Step.FACE);
														}}>
														Rescan Face
													</button>
												)}
											</div> */}
													</div>
												</div>
											</div>
										</>
									</StepContainer>
								)}

								{activeStep === Step.PROFILE && (
									<StepContainer active={true}>
										<>
											{console.log('step', 'GuestProfileForm')}
											<GuestProfileForm
												key={guestProfileFormKey}
												profile={currentProfile}
												docuSubmissionSkipped={false}
												countries={countries}
												fields={fields}
												originalGuestProfile={currentProfile}
												documentsSubmitted={null}
												onSubmitSuccess={(resp: any) =>
													setActiveStep(Step.SUCCESS)
												}
												onSubmitError={handleGuestProfileSubmitError}
												currentProfileIndex={0}
												profileDtl={currentProfile}
												disableUserFields={isExistingUser}
												ref={guestRef}
											/>
										</>
									</StepContainer>
								)}
								{activeStep === Step.SUCCESS && (
									<StepContainer active={true}>
										<>
											{console.log('step', 'SuccessVerification')}
											<SuccessVerification />
										</>
									</StepContainer>
								)}
								{activeStep === Step.ERROR && (
									<StepContainer active={true}>
										<>
											{console.log('step', 'ErrorVerification')}
											<ErrorVerification
												title={errorVerificationConfig?.title}
												msg={errorVerificationConfig?.msg}
												checks={errorVerificationConfig?.checks}
												onTryAgain={errorVerificationConfig?.onTryAgain}
												tryAgainLabel={errorVerificationConfig?.tryAgainLabel}
											/>
										</>
									</StepContainer>
								)}
							</>
						)}
					</div>
				</div>

				{showContinueButton && !showOnboardingModal && (
					<div
						className="fixed-bottom w-100 px-3 px-md-5 py-3 border-top theme-footer d-flex justify-content-center"
						style={{
							paddingBottom: 'env(safe-area-inset-bottom)',
						}}>
						{activeStep === Step.FACE_RESULT &&
						faceMatchStatus === 'success' ? (
							<DarkButton
								text={
									currentProfile?.isPrimary
										? t('Continue')
										: t('Continue Check-In')
								}
								filled
								hideArrow
								onClick={() => {
									setShowFaceResultSheet(false);
									setFaceMatchStatus(null);
									handleCallFinishStatus();
								}}
								disabled={isDocumentStepBlocked}
								override={lazyLoad}
								loader={footerButtonLoading}
								className="det-proceed-button"
							/>
						) : activeStep === Step.FACE_RESULT &&
						  faceMatchStatus === 'failed' ? (
							<DarkButton
								text={t('Rescan Face')}
								filled
								hideArrow
								onClick={() => {
									setFaceMatchStatus(null);
									setShowFaceResultSheet(false);
									setActiveStep(Step.FACE);
								}}
								disabled={isDocumentStepBlocked}
								override={lazyLoad}
								loader={footerButtonLoading}
								className="det-proceed-button"
							/>
						) : (
							<>
								<DarkButton
									text={
										activeStep === Step.SUCCESS
											? currentProfile?.isPrimary
												? t('Continue')
												: t('Complete Check-In')
											: t('Continue')
									}
									filled
									hideArrow
									onClick={proceedToNextStep}
									disabled={
										(activeStep === Step.INTRO && subStep === 'camera') ||
										activeStep === Step.FACE ||
										isDocumentStepBlocked
									}
									override={lazyLoad}
									loader={footerButtonLoading}
									className="det-proceed-button"
								/>
							</>
						)}
					</div>
				)}

				{showRegistrationSheet && (
					<>
						<div
							className="bottomsheet-overlay"
							onClick={() => setShowRegistrationSheet(false)}
						/>

						<div className="bottomsheet-container d-flex flex-column">
							<div className="sheet-handle-wrapper d-flex justify-content-center">
								<div className="sheet-handle"></div>
							</div>
							<div className="bottomsheet-content d-flex flex-column">
								<div className="bottomsheet-header text-center">
									<h5 className="mb-2 fw-bold">Registration Required</h5>
									<p className="text-muted small mb-4">
										It looks like this guest is not registered with Dubai DET.
										Would you like to register now to complete check-in?
									</p>
								</div>

								<div className="d-flex flex-column gap-3">
									<button
										className="btn btn-primary w-100"
										onClick={() => {
											// setTimeout(() => {
											// 	dispatch(setGuestVerificationLazyLoad(true));
											// }, 2000);
											setShowRegistrationSheet(false);
											setHideStepper(false);
											setActiveStep(Step.DOCUMENT);

											// setTimeout(() => {
											// 	dispatch(setGuestVerificationLazyLoad(false));
											// }, 3000);
										}}>
										Proceed Registration
									</button>
									<p className="text-muted small text-center">
										Already Registered?
									</p>
									<button
										className="btn btn-outline-primary w-100"
										onClick={() => {
											setShowRegistrationSheet(false);
											setActiveStep(Step.INTRO);
										}}>
										Rescan Face
									</button>
								</div>
							</div>
						</div>
					</>
				)}

				{showManualCheckinFrontDesk && (
					<>
						{/* BACKDROP */}
						<div
							className={styles.bottomSheetBackdrop}
							onClick={() => setShowManualCheckinFrontDesk(false)}
						/>

						{/* BOTTOM SHEET */}
						<div className={styles.bottomSheet}>
							{/* HEADER */}
							<div className="d-flex justify-content-between align-items-start">
								<h5 className="fw-semibold mb-1">
									{t('ManualCheckinTitle') || 'Manual Check-in Required'}
								</h5>
								<button
									className="btn-close"
									onClick={() => setShowManualCheckinFrontDesk(false)}
								/>
							</div>

							{/* CONTENT */}
							<p className="text-muted mt-3 mb-3">
								{t('ManualCheckinDescription') ||
									'Your pre-check-in was not completed successfully. Kindly visit the reception for further assistance.'}
							</p>

							{/* CONTACT FRONT DESK */}
							<div className="d-flex flex-column gap-3">
								<button
									className="btn btn-primary w-100"
									onClick={() => {
										bypassConfirmLeaveOnce();
										window.location.reload();
									}}>
									{t('ContinuePreCheckin') || 'Go to Home'}
								</button>
							</div>
							{/* <button
									className={styles.sheetOption}
									onClick={() => {
										console.log('Contact front desk');
										setShowManualCheckinFrontDesk(false);
										// You can add logic here to navigate to a support page or show contact info
									}}>
									<div>
										<div className="fw-semibold">
											{t('ContactFrontDesk') || 'Contact Front Desk'}
										</div>
										<small className="text-muted">
											{t('FrontDeskAssistance') ||
												'Get immediate assistance from front desk staff'}
										</small>
									</div>
								</button> */}
						</div>
					</>
				)}

				{showTakeSelfieModal && (
					<TakeSelfieModal
						show={showTakeSelfieModal}
						onClose={() => setShowTakeSelfieModal(false)}
						onUnderstand={async () => {
							await handleSdkInitialized();
						}}
						loading={false}
					/>
				)}
			</div>
		</div>
	);
};

export default GuestVerification;
